/* eslint-disable max-len */
// src/views/words-lists/ShareWordsListModal/ListOfStudentsInModal.tsx
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Avatar, Checkbox, List, Modal, Spin, Tooltip,
  message,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { TbInfoCircle } from 'react-icons/tb';
import i18n from '../../../../i18n';
import { pluralForm } from '../../../../utils/plural';

import {
  getListOfStudents,
  getStudentGroups,
} from '../../../student/slice/selectors';
import { fetchStudentGroups, fetchStudentsPage, refreshStudentsPage } from '../../../student/slice/actions';
import { StudentGroup, UserData } from '../../../../services/ApiService.types';
import ApiService from '../../../../services/ApiService';
import { getStudentGroupNames, sortGroupsByName } from '../../../../utils/studentGroups';
import { getApiErrorCode, getApiErrorMessage } from '../../../../utils/apiErrors';
import classes from './ShareWordsListModal.module.less';

interface ListOfStudentsInModalProps {
  wordsList: {
    id: number;
    name: string;
    sharedStudentIds: number[]; // Zamiast combinedIds
  };
  // Po każdej udanej zmianie udostępnień — okno odświeży listy po zamknięciu
  onShareUpdate?: () => void;
}

const ListOfStudentsInModal: React.FC<ListOfStudentsInModalProps> = ({
  wordsList,
  onShareUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentList = useSelector(getListOfStudents);
  const groups = useSelector(getStudentGroups);
  const sortedGroups = useMemo(() => sortGroupsByName(groups), [groups]);

  // Stan lokalny przechowuje ID studentów z dostępem (zamiast combinedIds)
  const [sharedStudentIds, setSharedStudentIds] = useState<number[]>(
    wordsList.sharedStudentIds || [],
  );
  const [pendingStudentIds, setPendingStudentIds] = useState<Set<number>>(new Set());
  const [pendingGroupIds, setPendingGroupIds] = useState<Set<number>>(new Set());
  const [isInitializing, setIsInitializing] = useState(true);

  const sharedSet = useMemo(() => new Set(sharedStudentIds), [sharedStudentIds]);

  // Eliminacja duplikatów na liście
  const students = useMemo(() => currentList.reduce((acc: UserData[], item: UserData) => {
    if (!acc.find((i) => i.id === item.id)) {
      acc.push(item);
    }

    return acc;
  }, []), [currentList]);

  const studentsById = useMemo(
    () => new Map<number, UserData>(students.map((student: UserData) => [student.id, student])),
    [students],
  );

  // Pobierz aktualny stan udostępnień przy montowaniu
  useEffect(() => {
    const loadShares = async () => {
      try {
        const response = await ApiService.wordsList.getShares(wordsList.id);
        setSharedStudentIds(response.data.sharedStudentIds);
      } catch (error) {
        console.error('Failed to load shares:', error);
        message.error(i18n.t('words-list:share.loadError'));
      } finally {
        setIsInitializing(false);
      }
    };

    loadShares();
  }, [wordsList.id]);

  // Pole wysyła to, co pokazuje: zaznaczenie dodaje listę, odznaczenie ją cofa. Przełączanie po stronie serwera
  // przy nieaktualnym stanie okna (np. lista dana w międzyczasie przez grupę) zabrałoby ją uczniowi
  const handleCheckboxChange = useCallback(async (e: CheckboxChangeEvent) => {
    const studentId = Number(e.target.value);
    const shouldShare = e.target.checked;

    if (!wordsList?.id) return;

    // Na ekranie od razu; przy błędzie wraca tylko ten uczeń, a zmiany przy innych zostają
    const markShared = (shared: boolean) => setSharedStudentIds((prev) => {
      const withoutStudent = prev.filter((id) => id !== studentId);

      return shared ? [...withoutStudent, studentId] : withoutStudent;
    });

    markShared(shouldShare);

    // Oznacz jako "pending"
    setPendingStudentIds((prev) => new Set(prev).add(studentId));

    try {
      // Wynik jest pewny bez pełnego składu w odpowiedzi: po „dodaj” uczeń ma listę, po „cofnij” nie ma
      if (shouldShare) {
        await ApiService.wordsList.addSharesToStudent(studentId, [wordsList.id]);
      } else {
        await ApiService.wordsList.revokeSharesFromStudent(studentId, [wordsList.id]);
      }

      // Powiadom rodzica o zmianie
      if (onShareUpdate) {
        onShareUpdate();
      }

      message.success(shouldShare ? t('words-list:share.shared') : t('words-list:share.revoked'));
    } catch (error) {
      console.error('Share error, rolling back:', error);
      markShared(!shouldShare);

      // Ucznia już nie ma (np. usunięty w drugiej karcie) — po odświeżeniu zniknie z okna i z grup
      if (getApiErrorCode(error) === 'STUDENT_NOT_FOUND') {
        message.error(getApiErrorMessage(error, t('student:gone')));
        dispatch(refreshStudentsPage());
        dispatch(fetchStudentGroups());
      } else {
        message.error(t('words-list:share.changeError'));
      }
    } finally {
      // Usuń z "pending"
      setPendingStudentIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(studentId);

        return newSet;
      });
    }
  }, [dispatch, wordsList, onShareUpdate, t]);

  const setGroupPending = useCallback((groupId: number, isPending: boolean) => {
    setPendingGroupIds((prev) => {
      const newSet = new Set(prev);

      if (isPending) {
        newSet.add(groupId);
      } else {
        newSet.delete(groupId);
      }

      return newSet;
    });
  }, []);

  // Grupa usunięta w międzyczasie (np. w drugiej karcie) znika z okna po ponownym wczytaniu grup
  const handleGroupError = useCallback((error: unknown, fallback: string) => {
    console.error('Group share error:', error);

    if (getApiErrorCode(error) === 'GROUP_NOT_FOUND') {
      message.error(getApiErrorMessage(error, t('student:groups.messages.gone')));
      dispatch(fetchStudentGroups());
    } else {
      message.error(fallback);
    }
  }, [dispatch, t]);

  // Dopisuje listę członkom grupy, którzy jej nie mają. Zaznaczenia przy uczniach — według odpowiedzi serwera
  const shareWithGroup = useCallback(async (group: StudentGroup) => {
    setGroupPending(group.id, true);

    try {
      const { data } = await ApiService.wordsList.shareWithGroup(wordsList.id, group.id);
      const count = data.addedStudentIds.length;

      setSharedStudentIds(data.sharedStudentIds);

      if (onShareUpdate) {
        onShareUpdate();
      }

      message.success(count > 0
        ? t(`words-list:share.groupShared.${pluralForm(count)}`, { n: count, group: group.name })
        : t('words-list:share.groupAllHave', { group: group.name }));
    } catch (error) {
      handleGroupError(error, t('words-list:share.groupShareError'));
    } finally {
      setGroupPending(group.id, false);
    }
  }, [wordsList.id, onShareUpdate, handleGroupError, setGroupPending, t]);

  const revokeFromGroup = useCallback(async (group: StudentGroup) => {
    setGroupPending(group.id, true);

    try {
      const { data } = await ApiService.wordsList.revokeFromGroup(wordsList.id, group.id);
      const count = data.revokedStudentIds.length;

      setSharedStudentIds(data.sharedStudentIds);

      if (onShareUpdate) {
        onShareUpdate();
      }

      message.success(count > 0
        ? t(`words-list:share.groupRevoked.${pluralForm(count)}`, { n: count, group: group.name })
        : t('words-list:share.groupNoneHad', { group: group.name }));
    } catch (error) {
      handleGroupError(error, t('words-list:share.groupRevokeError'));
    } finally {
      setGroupPending(group.id, false);
    }
  }, [wordsList.id, onShareUpdate, handleGroupError, setGroupPending, t]);

  // Cofnięcie zabiera listę wszystkim obecnym członkom grupy — także tym, którzy dostali ją osobno albo przez
  // inną grupę (serwer nie pamięta, którą drogą). Dlatego najpierw pytamy, wymieniając ich z imienia i nazwiska
  const confirmRevokeFromGroup = useCallback((group: StudentGroup) => {
    const losingList = group.studentIds
      .filter((id) => sharedSet.has(id))
      .map((id) => studentsById.get(id))
      .filter((student): student is UserData => Boolean(student));

    Modal.confirm({
      title: t('words-list:share.revokeGroup.title', { group: group.name }),
      content: (
        <>
          <p>{t('words-list:share.revokeGroup.losing', { list: wordsList.name })}</p>
          <ul className={classes.confirmNames}>
            {losingList.map((student) => (
              <li key={student.id}>{student.name} {student.surname}</li>
            ))}
          </ul>
          <p>{t('words-list:share.revokeGroup.alsoOthers')}</p>
        </>
      ),
      okText: t('words-list:share.revokeGroup.ok'),
      cancelText: t('common:buttons.cancel'),
      okButtonProps: { danger: true },
      onOk: () => revokeFromGroup(group),
    });
  }, [sharedSet, studentsById, wordsList.name, revokeFromGroup, t]);

  useEffect(() => {
    dispatch(fetchStudentsPage());
    dispatch(fetchStudentGroups());
  }, [dispatch]);

  // Ilu uczniów ma tę listę — licznik w nagłówku karty uczniów
  const sharedCount = students.filter((student: UserData) => sharedSet.has(student.id)).length;

  if (isInitializing) {
    return (
      <div className={classes.loadingContainer}>
        <Spin tip={t('words-list:share.loading')} />
      </div>
    );
  }

  return (
    <div className={classes.shareContent}>
      {/* Karta uczniów, w nagłówku licznik: ilu z nich ma tę listę */}
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.cardTitle}>{t('words-list:share.students')}</span>
          <span className={classes.counter} title={t('words-list:share.sharedCountTitle')}>
            {t('words-list:share.countOf', { n: sharedCount, total: students.length })}
          </span>
        </div>

        {/* Serwer oddaje wszystkich uczniów naraz — lista bez doładowywania, przewija się w swoim polu */}
        <div className={classes.scrollableContainer}>
          <List
            dataSource={students}
            renderItem={(student: UserData) => {
              if (!student.id) {
                console.warn('Student without ID:', student);

                return null;
              }

              const isShared = sharedSet.has(student.id);
              const isPending = pendingStudentIds.has(student.id);

              return (
                <List.Item
                  key={student.id}
                  tabIndex={0}
                  role="button"
                  className={classNames(classes.listItem, { [classes.shared]: isShared })}
                >
                  <List.Item.Meta
                    avatar={(
                      <Avatar className={classes.avatar}>
                        {student.name?.[0]?.toUpperCase()}
                        {student.surname?.[0]?.toUpperCase()}
                      </Avatar>
                    )}
                    title={`${student.name} ${student.surname}`}
                  />
                  {/* Przy polu, jak na ekranie uczniów; nad polem rozciągniętym na cały wiersz, żeby dymek łapał kursor */}
                  <div className={classes.groupTags}>
                    {getStudentGroupNames(sortedGroups, student.id).map((groupName) => (
                      <Tooltip key={groupName} title={t('student:list.belongsTo', { group: groupName })}>
                        <span className={classes.groupTag}>{groupName}</span>
                      </Tooltip>
                    ))}
                  </div>
                  <Checkbox
                    onChange={handleCheckboxChange}
                    checked={isShared}
                    value={student.id}
                    className={classes.mark}
                    disabled={isPending}
                  />
                </List.Item>
              );
            }}
          />
        </div>
      </div>

      {/* Grupy pod uczniami — lista częściej trafia do pojedynczego ucznia niż do całej grupy */}
      {sortedGroups.length > 0 && (
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>{t('words-list:share.groups')}</span>
          </div>
          <div className={classes.groupsSection}>
            {sortedGroups.map((group) => {
              const total = group.studentIds.length;
              // Ilu członków grupy ma tę listę
              const withList = group.studentIds.filter((id) => sharedSet.has(id)).length;
              const hasAll = total > 0 && withList === total;
              const isGroupPending = pendingGroupIds.has(group.id);

              // Pole grupy działa jak „zaznacz wszystkich” nad jej uczniami: pełne — listę mają wszyscy, kreska — część,
              // puste — nikt. Kliknięcie w puste albo w kreskę udostępnia brakującym, w pełne pyta i cofa. Pole stoi
              // zawsze na miejscu, żeby wiersze nie przeskakiwały; przy pustej grupie wyszarzone
              return (
                <Checkbox
                  key={group.id}
                  className={classNames(classes.groupRow, { [classes.shared]: hasAll, [classes.emptyGroup]: total === 0 })}
                  checked={hasAll}
                  indeterminate={withList > 0 && !hasAll}
                  disabled={isGroupPending || total === 0}
                  onChange={() => {
                    if (hasAll) {
                      confirmRevokeFromGroup(group);
                    } else {
                      shareWithGroup(group);
                    }
                  }}
                >
                  <span className={classes.groupName} title={group.name}>{group.name}</span>
                  <span className={classes.counter}>
                    {total > 0 ? t('words-list:share.countOf', { n: withList, total }) : t('words-list:share.emptyGroup')}
                  </span>
                </Checkbox>
              );
            })}
          </div>
        </div>
      )}

      {/* Przy wspólnych uczniach udostępnienie jednej grupie zmienia też licznik drugiej — pierwsze zdanie mówi dlaczego.
          Okno nie ma przycisku „Zapisz” — drugie mówi, że nie trzeba */}
      <p className={classes.footnote}>
        <TbInfoCircle className={classes.footnoteIcon} />
        <span>
          {sortedGroups.length > 0 && t('words-list:share.groupCounterHint')}
          {t('words-list:share.savedInstantly')}
        </span>
      </p>
    </div>
  );
};

export default ListOfStudentsInModal;
